// Globaler Scanner-Provider: hält die eine aktive Scan-Anfrage und rendert
// das Overlay lazy, damit zxing/WASM erst bei der ersten Nutzung geladen wird.

import { Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ScannerContext } from './ScannerContextCore';
import type { ScanRequest, ScanResult, ScannerContextValue } from './types';

const ScannerOverlay = lazy(() => import('./ScannerOverlay'));

interface ActiveScan {
  id: number;
  request: ScanRequest;
}

export function ScannerProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveScan | null>(null);
  const resolverRef = useRef<((result: ScanResult | null) => void) | null>(null);
  const seqRef = useRef(0);

  const settle = useCallback((result: ScanResult | null) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setActive(null);
    resolve?.(result);
  }, []);

  const scan = useCallback((request?: ScanRequest) => {
    return new Promise<ScanResult | null>((resolve) => {
      // Es gibt nur ein Overlay: eine evtl. laufende Anfrage wird abgebrochen
      resolverRef.current?.(null);
      resolverRef.current = resolve;
      seqRef.current += 1;
      setActive({ id: seqRef.current, request: request ?? {} });
    });
  }, []);

  const close = useCallback(() => settle(null), [settle]);

  const value = useMemo<ScannerContextValue>(
    () => ({ scan, close, isOpen: active !== null }),
    [scan, close, active],
  );

  return (
    <ScannerContext.Provider value={value}>
      {children}
      {active && (
        <Suspense fallback={null}>
          <ScannerOverlay key={active.id} request={active.request} onResolve={settle} />
        </Suspense>
      )}
    </ScannerContext.Provider>
  );
}
